import * as Popover from '@radix-ui/react-popover';
import type { AutomationCategoryResponse } from '@reach/shared-types/automation-category';
import { ChevronDown, Tag } from 'lucide-react';
import { useState } from 'react';
import { CategoryEditDialog } from '../CategoryEditDialog/CategoryEditDialog';
import { keepOpenWhileEditingCategory } from '../CategoryEditDialog/categoryEditDialogTarget';
import { CategoryPill } from '../CategoryPill/CategoryPill';
import { CategoryCreateForm } from '../CategoryCreateForm/CategoryCreateForm';
import styles from './CategoryFilterMenu.module.css';

interface CategoryFilterMenuProps {
  categories: AutomationCategoryResponse[];
  selectedCategoryIds: string[];
  toggleCategory: (categoryId: string) => void;
  clearCategories: () => void;
}

/**
 * Multi-select of the partner's categories. Templates with any selected category are shown. Used
 * in the automation templates toolbar.
 */
export function CategoryFilterMenu({
  categories,
  selectedCategoryIds,
  toggleCategory,
  clearCategories,
}: CategoryFilterMenuProps) {
  const [categoryBeingEdited, setCategoryBeingEdited] = useState<AutomationCategoryResponse | null>(
    null,
  );
  const selectedCount = selectedCategoryIds.length;
  const hasSelection = selectedCount > 0;
  const hasCategories = categories.length > 0;
  const selectedSummary = `${selectedCount} selected`;

  return (
    <>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className={styles.trigger} data-active={hasSelection}>
            <Tag aria-hidden />
            Categories
            {hasSelection && <span className={styles.countPill}>{selectedCount}</span>}
            <ChevronDown aria-hidden />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={styles.menu}
            align="start"
            sideOffset={8}
            onInteractOutside={keepOpenWhileEditingCategory}>
            {hasCategories && (
              <fieldset className={styles.options}>
                <legend>Filter by category</legend>
                {categories.map(category => (
                  <label key={category.id}>
                    <input
                      type="checkbox"
                      checked={selectedCategoryIds.includes(category.id)}
                      onChange={() => toggleCategory(category.id)}
                    />
                    <CategoryPill category={category} />
                  </label>
                ))}
              </fieldset>
            )}
            {hasSelection && (
              <footer className={styles.footer}>
                <span>{selectedSummary}</span>
                <button type="button" onClick={clearCategories}>
                  Clear
                </button>
              </footer>
            )}
            <CategoryCreateForm isFirstCategory={!hasCategories} onCreated={setCategoryBeingEdited} />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {categoryBeingEdited && (
        <CategoryEditDialog
          category={categoryBeingEdited}
          onClose={() => setCategoryBeingEdited(null)}
        />
      )}
    </>
  );
}
